import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import PhotoPreviewView from './content-photo-preview-view';
import Validate from '../../../../utils/validate';

const PhotoPreviewContainer = ({
  entityId,
  photo,
  photoId,
  onRemovePhoto,
  onChangePhoto,
  onCreatePhoto,
  showInfo,
  style,
  onImageChangesStatusChange,
}) => {
  const placeHolder = 'https://res.cloudinary.com/zicasso/image/upload/v1545258210/Logo-%20Marketing%20Materials/PNG/Zicasso_Logo_no_sub_black_small.png';
  
  const initializeFormValues = () => {
    const values = { ...photo };
    if (photo.sourceUri && !values.uri) {
      values.uri = photo.sourceUri;
    }
    return values;
  };
  
  const [formValues, setFormValues] = useState(initializeFormValues);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const loadedRef = useRef(false);

  // Derive fileName from photo data
  const getFileName = () => {
    const { sourceType, sourceUri } = photo;
    if (!sourceUri || sourceType !== 'Cloudinary') return undefined;
    
    const splitFile = sourceUri.endsWith('.jpg')
      ? sourceUri.slice(0, -4).split('/')
      : sourceUri.split('/');
    const { length } = splitFile;
    return `${splitFile[length-2]}/${splitFile[length-1]}`;
  };

  const validUri = useMemo(() => {
    if (!formValues.uri) return true;
    return Validate.imageUrl(formValues.uri);
  }, [formValues.uri]);

  const hasValidExistingPhoto = !!photoId && !!photo.sourceUri;
  const hasPhotoIdWithoutSource = !!photoId && !photo.sourceUri;
  const hasNoPhoto = !photoId;

  useEffect(() => {
    // Sync form values when photoId changes
    if (formValues.id !== photoId) {
      setFormValues(initializeFormValues());
      setHasUnsavedChanges(false);
    }
    
    // Notify parent of unsaved changes
    if (onImageChangesStatusChange) {
      onImageChangesStatusChange(hasUnsavedChanges);
    }
  }, [photoId, hasUnsavedChanges, onImageChangesStatusChange, formValues.id]);

  const onChange = (label, text) => {
    setFormValues((prev) => ({ ...prev, [label]: text }));
    setHasUnsavedChanges(true);
  };

  const removePhoto = () => {
    onRemovePhoto(entityId, photoId);
    loadedRef.current = false;
    setHasUnsavedChanges(false);
  };

  const onLoad = () => {
    if (formValues.uri && validUri) {
      loadedRef.current = true;
    }
  };

  const handleSaveImage = async () => {
    setIsSaving(true);
    
    try {
      if (hasValidExistingPhoto) {
        await onChangePhoto(formValues);
      } else if (hasNoPhoto || hasPhotoIdWithoutSource) {
        if (!formValues.uri || !validUri) {
          throw new Error('Valid URI required for new images');
        }
        await onCreatePhoto(formValues);
      }
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving image:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const canSaveImage = !isSaving && hasUnsavedChanges && (
    hasValidExistingPhoto || 
    ((hasNoPhoto || hasPhotoIdWithoutSource) && formValues.uri && validUri)
  );

  const buttonText = isSaving ? 'Saving...' : (hasValidExistingPhoto ? 'Update Image' : 'Add Image');

  return (
    <PhotoPreviewView 
      alt={formValues.alt}
      fileName={getFileName()}
      gravity={formValues.gravity}
      info={formValues}
      onChange={onChange}
      onLoad={onLoad}
      photoId={photoId}
      placeHolder={placeHolder}
      readOnly={!!formValues.sourceUri}
      removePhoto={removePhoto}
      showDelete={!!formValues.sourceUri}
      showInfo={showInfo}
      src={formValues.uri || formValues.sourceUri} 
      style={{ ...style }} 
      title={formValues.title}
      validUri={validUri}
      canSaveImage={canSaveImage}
      buttonText={buttonText}
      onSaveImage={handleSaveImage}
    />
  );
};

PhotoPreviewContainer.propTypes = {
  photo: PropTypes.object,
  photoId: PropTypes.string,
  onRemovePhoto: PropTypes.func.isRequired,
  onChangePhoto: PropTypes.func.isRequired,
  onCreatePhoto: PropTypes.func.isRequired,
  showInfo: PropTypes.bool,
  style: PropTypes.object,
  onImageChangesStatusChange: PropTypes.func,
};

PhotoPreviewContainer.defaultProps = {
  photo: {},
  photoId: undefined,
  showInfo: true,
  style: {},
  onImageChangesStatusChange: undefined,
};

export default PhotoPreviewContainer;
